class FocusableCarousel {
    _activeIndex = 0;
    _wrapper = null;
    _prev = null;
    _next = null;
    _direction = '';
    _amount = 0;
    _visibleQuery = 'div.swiper-slide.swiper-slide-visible a';
    _invisibleQuery = 'div.swiper-slide:not(.swiper-slide-visible) a';
    /**
     *
     * @param wrapper Swiper
     */
    constructor(wrapper) {
        this._wrapper = wrapper;
        this.__initWrapperEvents();
        this._prev = this._wrapper.el.querySelector('a.carousel-prev');
        this._next = this._wrapper.el.querySelector('a.carousel-next');
        this._prev.setAttribute('role', 'button');
        this._next.setAttribute('role', 'button');
        this._prev.setAttribute('aria-label', 'Previous Slides');
        this._next.setAttribute('aria-label', 'Next Slides');
        // Handle keyboard events for the next/prev arrows...
        // next arrow -> next page (enter/space), or shift+tab to previous slide
        // prev arrow -> previous page (enter/space), or tab to next element on the page
        // Due to the way the HTML is created for Swiper, Prev/Next arrow is actually last in the DOM
        this._next.addEventListener(
            'keydown',
            (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    this._wrapper.slideNext();
                    return true;
                } else if (event.key === 'Tab' && event.shiftKey) {
                    event.preventDefault();
                    this._wrapper.visibleSlides[this._wrapper.visibleSlides.length - 1]
                        .querySelector('a.link')
                        .focus();
                    return true;
                }
                return false;
            },
            true,
        );
        this._prev.addEventListener(
            'keydown',
            (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    this._wrapper.slidePrev();
                    return true;
                } else if (event.key === 'Tab' && !event.shiftKey) {
                    event.preventDefault();
                    this._wrapper.visibleSlides[0].querySelector('a.link').focus();
                    return true;
                } else if (event.key === 'Tab' && event.shiftKey) {
                    return true;
                }
                return false;
            },
            true,
        );
        // We need to capture the 'active index' (selected slide)
        this._next.addEventListener('focus', () => {
            this._activeIndex = -1;
        });
        this._prev.addEventListener('focus', () => {
            this._activeIndex = -1;
        });
        // Add necessary attributes (tabindex), or remove them
        this.setItemsFocusable(this._visibleQuery);
        this.setItemsUnFocusable(this._invisibleQuery);

        this.__addElementEvents();
        // Set arrows focusable/unfocusable based on current slide list
        this.updateArrows();
    }
    __initWrapperEvents() {
        // Hook into wrapper event system to update accessibility/keyboard navigation
        this._wrapper.on('reachBeginning', () => {
            this._prev?.setAttribute('tabindex', '-1');
        });
        this._wrapper.on('reachEnd', () => {
            this._next?.setAttribute('tabindex', '-1');
        });
        this._wrapper.on('slideNextTransitionEnd', () => {
            this.__addElementEvents();
            this.setItemsUnFocusable(this._invisibleQuery);
            this.setItemsFocusable(this._visibleQuery);
            this.updateArrows();
            //this._wrapper.visibleSlides[0].querySelector('a.link').focus(); // Commented out to prevent focus on first slide
            this._activeIndex = 0;
        });
        this._wrapper.on('slidePrevTransitionEnd', () => {
            this.__addElementEvents();
            this.setItemsUnFocusable(this._invisibleQuery);
            this.setItemsFocusable(this._visibleQuery);
            this.updateArrows();
            //this._wrapper.visibleSlides[this._wrapper.visibleSlides.length - 1].querySelector('a.link').focus(); // Commented out to prevent focus on last slide
            this._activeIndex = this._amount - 1;
        });
    }
    updateArrows() {
        if (!this._wrapper.isEnd) {
            this._next?.setAttribute('tabindex', '0');
        } else {
            this._next?.setAttribute('tabindex', '-1');
        }
        if (!this._wrapper.isBeginning) {
            this._prev?.setAttribute('tabindex', '0');
        } else {
            this._prev?.setAttribute('tabindex', '-1');
        }
    }
    setItemsFocusable(query) {
        const items = this._wrapper.el.querySelectorAll(query);
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (!item) {
                continue;
            }
            item.setAttribute('tabindex', '0');
            if (!item.classList.contains('focusable')) {
                item.classList.add('focusable');
            }
            item.setAttribute('role', 'button');
            item.setAttribute('aria-hidden', 'false');
        }
    }
    setItemsUnFocusable(query) {
        const items = this._wrapper.el.querySelectorAll(query);
        items.forEach((item) => {
            item.setAttribute('tabindex', '-1');
            item.setAttribute('aria-hidden', 'true');
            // Only so there is no confusion in the browser.
            item.classList.remove('focusable');
        });
    }
    getFocusedIndex(event) {
        this._activeIndex = this._wrapper.realIndex % this._amount;
    }
    // Add events to each visible slide, add 'has-listeners' attribute so we don't add multiple
    // events to the same element.
    __addElementEvents() {
        this._amount = this._wrapper.visibleSlides.length;
        this._wrapper.el.querySelectorAll(this._visibleQuery).forEach((item) => {
            if (!item.hasAttribute('has-listeners')) {
                item.setAttribute('has-listeners', 'true');
                item.addEventListener('focus', (event) => {
                    this.getFocusedIndex(event);
                });
                item.addEventListener('keydown', (event) => {
                    if (event.key === 'Tab' && !event.shiftKey) {
                        this._direction = 'right';
                    } else if (event.key === 'Tab' && event.shiftKey) {
                        this._direction = 'left';
                    }
                    if (
                        this._activeIndex === 0 &&
                        this._direction === 'left' &&
                        !this._wrapper.isBeginning
                    ) {
                        event.preventDefault();
                        this._prev.focus();
                        return;
                    } else if (
                        this._activeIndex === this._amount - 1 &&
                        this._direction === 'right' &&
                        !this._wrapper.isEnd
                    ) {
                        event.preventDefault();
                        this._next.focus();
                        return;
                    } else if (
                        this._activeIndex === this._amount - 1 &&
                        this._direction === 'right' &&
                        this._wrapper.isEnd
                    ) {
                        this._next.setAttribute('tabindex', '-1');
                        this._prev.setAttribute('tabindex', '-1');
                    }
                    if (this._direction === 'right') {
                        this._wrapper.realIndex++;
                    } else {
                        this._wrapper.realIndex--;
                    }
                });
            }
        });
    }
}
FocusableCarousel._counter = 0;
// For some reason 2 of the carousels are constructed twice, so we don't want to duplicate the instance.
FocusableCarousel._carousels = [];
/**
 * Simple static function so we don't need to instantiate the Carousel in every place.
 * Also tracks the loaded carousels so we don't get duplicates, adding multiple events to everything.
 * @param wrapper Swiper
 */
FocusableCarousel.init = function (wrapper) {
    if (!wrapper || !wrapper.el) {
        return null;
    }
    if (FocusableCarousel._carousels.indexOf(wrapper) === -1) {
        FocusableCarousel._carousels.push(wrapper);
        FocusableCarousel._counter++;
        return new FocusableCarousel(wrapper);
    }
};

/**
 *
 * @param wrapper Swiper instance
 * @constructor
 *
 * NOTE: Tabbing endlessly through the carousel is purposefully done in ./accessibilityCarousel.js
 * Swiper 4.5
 */
export function UpdateFocusableCarousel(wrapper) {
    try {
        FocusableCarousel.init(wrapper);
    } catch (e) {}
}

/* handle carousel auto scroll when it reaches the end of the current slide while using keyboard tab - Assuming there are more slides ahead */
let keyTabPressed = false;

$('body').on('keydown', '.js-le-carousel .carousel .video-carousel-box .wrap a', function (e) {
    const keyCode = e.keyCode || e.which;
    let focusNext = null; // by default, assume carousel editing is off

    if ($(this).closest('.video-carousel-box').hasClass('editOn') && keyCode === 9 && !e.shiftKey) {
        // keyboard tab next
        e.preventDefault();
        if (!keyTabPressed) {
            keyTabPressed = true;
            if (!$(this).closest('.video-carousel-box').hasClass('editOn')) {
                /* disable the INPUT focus if user is NOT in carousel EDITING mode */
                $(this).closest('.carousel-container').find('input').attr('tabindex', '-1');
            } else {
                focusNext = 'input:checkbox[name=editor]';
            }

            let selectedSlideVisible = $(this).parent('.swiper-slide').index();
            let mySwiper = $(this).closest('.wrap')[0].swiper;
            let visibleItems = mySwiper.visibleSlidesIndexes.length;

            /* edit mode : when user reaches the end of the carousel while editing, the next focus will be back to the REMOVE link */
            if ($(this).closest('.video-carousel-box').hasClass('editOn')) {
                if (!$(this).parent('.swiper-slide').next().is('div')) {
                    e.preventDefault();
                    $(this).closest('.carousel').find('.edit-carousel').focus();
                    keyTabPressed = false;
                    return;
                }
            }

            /* perform auto scroll to the next slide */
            if ((selectedSlideVisible + 1) % visibleItems === 0) {
                /* make sure the new slide from ajax - all hrefs are focus disable if it is in edit session */
                if ($(this).closest('.video-carousel-box').hasClass('editOn')) {
                    /* disable the INPUT focus if user is NOT in carousel EDITING mode */
                    $(this).closest('.wrap').find('a').attr('tabindex', '-1');
                }
            }
        }
    }
});

/* handle carousel editing function */
$('body').on('keydown', '.js-le-carousel .carousel .edit-carousel', function (e) {
    var keyCode = e.keyCode || e.which;
    if (keyCode == 13) {
        /* disable all the href focus */
        $(this).parent().find('a').attr('tabindex', '-1');

        /* enable all Input Checkbox focus  */
        $(this).parent().find('input:checkbox[name=editor]').attr('tabindex', '0');

        $(this).next().attr('tabindex', '0'); // turn on cancel link focus div
        $(this).click();
    } else if (e.shiftKey && keyCode == 9) {
        // auto cancel edit if focus is outside of current carousel
        $('.js-le-carousel .carousel .edit-cancel-carousel').click();
    }
});

/* handle carousel edit cancellation function */
$('body').on('keydown', '.js-le-carousel .carousel .edit-cancel-carousel', function (e) {
    var keyCode = e.keyCode || e.which;
    let mySwiper = $(this).siblings('.video-carousel-box').find('.wrap')[0].swiper;

    if (keyCode == 13) {
        /* keyboard enter to cancel the operation */

        /* slide to the beginning of the slide  */
        mySwiper.slideTo(1, false, false);

        /* restore everything back to non editing mode */
        /* enable all the href focus */
        $(this).parent().find('a').attr('tabindex', '0');

        /* disable all Input Checkbox focus  */
        $(this).parent().find('input:checkbox[name=editor]').attr('tabindex', '-1');
        $(this).parent().find('input:checkbox[name=editor]').attr('checked', false);
        $(this).attr('tabindex', '-1'); // turn off cancel link focus div

        $(this).click();
    } else if (keyCode == 9) {
        /* keyboard tab away - as in changing the edited item (repeat the carousel editing) */
        /* bring the slide to the beginning */
        mySwiper.slideTo(1, false, false);
    }
});

/* handle carousel item selection for removal function */
$('body').on('keypress', '.js-le-carousel .carousel input:checkbox[name=editor]', function (e) {
    var keyCode = e.keyCode || e.which;
    if (keyCode == 13) {
        if ($(this).attr('checked')) {
            $(this).attr('checked', false);
            e.stopPropagation();
            $(this).parent().children('.overlay').css('opacity', '1');
            $(this)
                .parent()
                .children('.overlay')
                .css(
                    'background',
                    'linear-gradient(180deg, rgba(0,0,0,0.5508578431372548) 0%, rgba(0,0,0,0) 35%, rgba(0,0,0,0) 100%)',
                );
        } else {
            $(this).attr('checked', true);
            e.stopPropagation();
            $(this).parent().children('.overlay').css('opacity', '.55');
            $(this).parent().children('.overlay').css('background-color', '#1D1D1D');
        }
    }
});

/* detect Firefox browser to handle the <video> not tabbable issue */
let myBrowser = navigator.userAgent.match(/firefox|fxios/i);
if (myBrowser) {
    $('body').on('focus keydown', 'section#latest-episodes .grid article a', function (e) {
        $(this).find('.supra-wrapper video').hide();
    });

    $('body').on('mouseenter', 'section#latest-episodes .grid article a', function (e) {
        $(this).find('.supra-wrapper video').show();
    });

    $('body').on(
        'focus keydown',
        '.js-le-carousel .carousel .video-carousel-box .wrap a',
        function (e) {
            $(this).find('.supra-wrapper video').hide();
        },
    );

    $('body').on(
        'mouseenter',
        '.js-le-carousel .carousel .video-carousel-box .wrap a',
        function (e) {
            $(this).find('.supra-wrapper video').show();
        },
    );
}

/* edge case - mouse click and keyboard tab operation : handle a case where no matter how many time a user clicked the next button on a given carousel,
follows by using keyboard tabbing to navigate ... User can resume where he/she previously left off. */

$('body').mousedown(function () {
    /* disable all carousel edit options */
    $('.edit-container').find('input[type=checkbox]').attr('tabindex', '-1');

    /* enable all carousel items that are in focus */
    //$("section").find('.swiper-slide-visible').find('a').attr("tabindex", '0');
});
